export function resolveCatalogSectionOrderKey(catalogProducts, sectionKey) {
  const section = (catalogProducts.sections ?? []).find((item) => item.key === sectionKey);

  return section?.orderKey || null;
}

export const DEFAULT_CATALOG_EMPLOYEE_COUNT = 1;
export const CATALOG_ORDER_MIN_BULK = 10;
export const CATALOG_ORDER_MIN_SINGLE = 1;

const normalizeCatalogTitle = (value) => String(value ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export function parseCatalogFixedPrice(priceText) {
  if (priceText == null || String(priceText).trim() === '') {
    return null;
  }

  const digits = String(priceText).replace(/[^\d]/g, '');

  return digits ? Number(digits) : null;
}

export function buildCatalogPriceRules(catalogProducts) {
  const rules = {};

  (catalogProducts.sections ?? []).forEach((section) => {
    (catalogProducts[section.key] ?? []).forEach((product) => {
      if (!product.prices || !product.orderProductKey) {
        return;
      }

      rules[String(product.orderProductKey)] = product.prices;
    });
  });

  return rules;
}

export function buildCatalogFixedPriceRules(catalogProducts) {
  const rules = {};

  (catalogProducts.sections ?? []).forEach((section) => {
    (catalogProducts[section.key] ?? []).forEach((product) => {
      if (!product.orderProductKey) {
        return;
      }

      const fixedPrice = parseCatalogFixedPrice(product.priceText);

      if (fixedPrice != null) {
        rules[String(product.orderProductKey)] = fixedPrice;
      }
    });
  });

  return rules;
}

export const CATALOG_CONTEXT_SECTIONS = [
  'analytics',
  'development',
  'professionalDevelopment',
  'expertSupport',
];

const ORDER_TABLE_SECTIONS = {
  test: ['tests'],
  courses: ['development'],
  professionalDevelopment: ['professionalDevelopment'],
  expertSupport: ['expertSupport'],
  analytics: ['analytics'],
};

export function getCatalogSectionProducts(sectionKey, catalogProducts) {
  return catalogProducts[sectionKey] ?? [];
}

export function resolveCatalogProductOrderKey(catalogProduct, sectionKey, index) {
  if (catalogProduct.orderProductKey) {
    return String(catalogProduct.orderProductKey);
  }

  return `catalog_${sectionKey}_${index}`;
}

export function createCatalogOrderRow(catalogProduct, productId) {
  const fixedPrice = parseCatalogFixedPrice(catalogProduct.priceText);

  return {
    product: catalogProduct.title,
    product_id: productId,
    free: 0,
    quota: 0,
    annual_price: fixedPrice ?? '',
    original_price: fixedPrice ?? '',
    total_price: 0,
  };
}

function getCatalogItemsForTable(tableKey, catalogProducts) {
  const sectionKeys = ORDER_TABLE_SECTIONS[tableKey] ?? [];

  return sectionKeys.flatMap((sectionKey) => (
    (catalogProducts[sectionKey] ?? []).map((product) => ({ ...product, sectionKey }))
  ));
}

export function findCatalogProductForOrderRow(tableKey, row, catalogProducts) {
  const catalogItems = getCatalogItemsForTable(tableKey, catalogProducts);

  const byProductKey = catalogItems.find((product) => (
    product.orderProductKey && String(product.orderProductKey) === String(row.product_id)
  ));

  if (byProductKey) {
    return byProductKey;
  }

  const rowTitle = normalizeCatalogTitle(row.product);

  return catalogItems.find((product) => (
    normalizeCatalogTitle(product.title) === rowTitle
    || rowTitle.includes(normalizeCatalogTitle(product.title).slice(0, 40))
    || normalizeCatalogTitle(product.title).includes(rowTitle.slice(0, 40))
  )) ?? null;
}

export function resolveOrderProductMinCount(catalogProduct) {
  if (!catalogProduct) {
    return 0;
  }

  if (catalogProduct.priceText && !catalogProduct.prices) {
    return CATALOG_ORDER_MIN_SINGLE;
  }

  if (catalogProduct.skipEmployeeQuotaMin) {
    return CATALOG_ORDER_MIN_SINGLE;
  }

  if (!catalogProduct.prices) {
    return 0;
  }

  if (catalogProduct.sectionKey === 'professionalDevelopment') {
    return CATALOG_ORDER_MIN_SINGLE;
  }

  return CATALOG_ORDER_MIN_BULK;
}

export function resolveOrderRowMinCount(tableKey, row, catalogProducts) {
  const catalogProduct = findCatalogProductForOrderRow(tableKey, row, catalogProducts);

  if (!catalogProduct) {
    return 0;
  }

  return resolveOrderProductMinCount(catalogProduct);
}

export function findCatalogPricesForOrderRow(tableKey, row, catalogProducts) {
  const catalogProduct = findCatalogProductForOrderRow(tableKey, row, catalogProducts);

  return catalogProduct?.prices ?? null;
}

export function findCatalogFixedPriceForOrderRow(tableKey, row, catalogProducts) {
  const catalogProduct = findCatalogProductForOrderRow(tableKey, row, catalogProducts);

  return parseCatalogFixedPrice(catalogProduct?.priceText);
}

export function resolveOrderProductId(catalogProducts, catalogProduct, tableKey, rows) {
  if (!catalogProduct) {
    return null;
  }

  const catalogItems = getCatalogItemsForTable(tableKey, catalogProducts);
  const catalogItem = catalogItems.find((item) => (
    (catalogProduct.orderProductKey && String(item.orderProductKey) === String(catalogProduct.orderProductKey))
    || normalizeCatalogTitle(item.title) === normalizeCatalogTitle(catalogProduct.title)
  ));

  if (catalogItem?.orderProductKey) {
    return catalogItem.orderProductKey;
  }

  const catalogTitle = normalizeCatalogTitle(catalogProduct.title);

  const row = (rows ?? []).find((item) => {
    const rowTitle = normalizeCatalogTitle(item.product);

    return rowTitle === catalogTitle
        || rowTitle.includes(catalogTitle.slice(0, 40))
        || catalogTitle.includes(rowTitle.slice(0, 40));
  });

  return row?.product_id ?? null;
}

export function getTierPrice(prices, quantity) {
  if (!prices || quantity == null || quantity === '') {
    return null;
  }

  const normalizedQuantity = Number(quantity);

  if (Number.isNaN(normalizedQuantity)) {
    return null;
  }

  const tiers = Object.keys(prices)
      .map(Number)
      .filter((key) => !Number.isNaN(key))
      .sort((a, b) => a - b);

  if (!tiers.length) {
    return null;
  }

  const matchedTier = tiers.find((tier) => normalizedQuantity <= tier);
  const tierKey = matchedTier ?? tiers[tiers.length - 1];

  return prices[String(tierKey)] ?? prices[tierKey] ?? null;
}

export function formatCatalogPrice(value, options = {}) {
  if (value == null || value === '') {
    return '';
  }

  const formatted = `${Number(value).toLocaleString('ru-RU')} ₽`;

  return options.prefix ? `${options.prefix} ${formatted}` : formatted;
}

export function resolveCatalogSubscriptionProductId(catalogSubscription) {
  return catalogSubscription.title;
}

export function resolveCatalogSubscriptionInOrderForm(rows, catalogSubscription) {
  const productId = resolveCatalogSubscriptionProductId(catalogSubscription);

  const rowById = (rows ?? []).find((item) => String(item.product_id) === String(productId));

  if (rowById) {
    return { productId: rowById.product_id, row: rowById };
  }

  const rowByTitle = (rows ?? []).find((item) => (
    item.product === catalogSubscription.title
  ));

  if (rowByTitle) {
    return { productId: rowByTitle.product_id, row: rowByTitle };
  }

  return { productId, row: null };
}

export function createCatalogSubscriptionRow(catalogSubscription, productId) {
  const price = parseCatalogFixedPrice(catalogSubscription.price);

  return {
    product_id: productId,
    product: catalogSubscription.title,
    free: 0,
    has_access: false,
    quota: 0,
    annual_price: price ?? '',
    original_price: price ?? '',
    total_price: 0,
  };
}

export function resolveProductPriceDisplay(product, quota) {
  const fixedPrice = product.priceText != null && String(product.priceText).trim() !== ''
      ? String(product.priceText).trim()
      : null;

  if (fixedPrice) {
    return product.pricePrefix ? `${product.pricePrefix} ${fixedPrice}` : fixedPrice;
  }

  const price = getTierPrice(product.prices, quota);

  if (price == null) {
    return '';
  }

  return formatCatalogPrice(price, { prefix: product.pricePrefix });
}

export function resolveActiveCatalogSubscriptions(catalogSubscriptions = [], permittedSubscriptions = []) {
  const permittedByTitle = new Map(
      (permittedSubscriptions ?? [])
          .map((subscription) => [subscription.title, subscription])
          .filter(([title]) => title),
  );

  return (catalogSubscriptions ?? [])
      .map((subscription) => {
        const permittedSubscription = permittedByTitle.get(subscription.title);

        if (!permittedSubscription) {
          return null;
        }

        const assigned = permittedSubscription.assigned ?? 0;
        const available = permittedSubscription.available ?? 0;

        return {
          ...subscription,
          used: assigned,
          total: available + assigned,
          expires: `Истекает ${permittedSubscription.expired_at}`,
        };
      })
      .filter(Boolean);
}
